import type { MembershipStatus } from "../../lib/types";
import {
  FLOW_LIST_OPERATORS,
  FLOW_PRESENCE_OPERATORS,
  FLOW_VALUELESS_OPERATORS,
  flowBaseFieldValueTypes,
  flowOperatorsByValueType,
  type FlowBaseField,
  type FlowCondition,
  type FlowConditionEvaluation,
  type FlowConditionLiteral,
  type FlowConditionOperand,
  type FlowConditionOperator,
  type FlowConditionOutcome,
  type FlowEvaluatedInput,
  type FlowMessageEvent,
  type FlowTimeEvent,
  type FlowValueType,
} from "./contracts";
import { flowConditionSchema } from "./schema";

/**
 * DSL declarativa e tipada de condições.
 *
 * NÃO existe `eval`, `Function`, SQL, JavaScript, template executável nem
 * expressão livre fornecida pelo usuário. Toda avaliação é pura: ela recebe um
 * contexto sanitizado — inclusive o relógio, em `now` — e devolve expressão,
 * entradas usadas, resultado e motivo.
 *
 * Valor ausente NUNCA cai silenciosamente em verdadeiro ou falso: o resultado é
 * `"unknown"`, explícito na trilha, e a aresta correspondente não é escolhida.
 */

export interface FlowRelatedMembershipView {
  funnelId: string;
  status: MembershipStatus;
}

export interface FlowEvaluationContext {
  ownerId: string;
  leadId: string;
  funnelId: string;
  companyId?: string;
  contactId?: string;
  /** Relógio fornecido pelo chamador. O avaliador nunca lê `Date`. */
  now: string;
  stageId?: string;
  membershipStatus?: MembershipStatus;
  baseFields: Partial<Record<FlowBaseField, string | number | boolean | null>>;
  customFields: Record<string, FlowConditionLiteral | null | undefined>;
  tagIds: readonly string[];
  triggerIds: readonly string[];
  replyClassification?: string;
  approachType?: string;
  messageEvents: readonly FlowMessageEvent[];
  completedTaskNodeIds: readonly string[];
  minutesSinceEvent: Partial<Record<FlowTimeEvent, number>>;
  minutesInStage?: number;
  relatedMemberships: readonly FlowRelatedMembershipView[];
}

export interface FlowConditionEvaluationOptions {
  /**
   * Gancho de redação: quando devolve `true`, o valor do operando não é
   * copiado para a trilha. A auditoria continua registrando presença e tipo.
   */
  redactOperand?: (operand: FlowConditionOperand) => boolean;
}

export function operandKey(operand: FlowConditionOperand): string {
  switch (operand.kind) {
    case "base_field":
      return `base_field:${operand.field}`;
    case "custom_field":
      return `custom_field:${operand.fieldId}`;
    case "reply_classification":
      return `reply_classification:${operand.catalogVersionId}`;
    case "approach_type":
      return `approach_type:${operand.catalogVersionId}`;
    case "message_event":
      return `message_event:${operand.event}`;
    case "task_completed":
      return `task_completed:${operand.taskNodeId}`;
    case "time_since_event":
      return `time_since_event:${operand.event}`;
    case "related_membership":
      return `related_membership:${operand.funnelId}`;
    default:
      return operand.kind;
  }
}

export function operandValueType(operand: FlowConditionOperand): FlowValueType {
  switch (operand.kind) {
    case "base_field":
      return flowBaseFieldValueTypes[operand.field];
    case "custom_field":
      return operand.valueType;
    case "tag":
    case "trigger":
      return "set";
    case "stage":
    case "membership_status":
    case "reply_classification":
    case "approach_type":
    case "related_membership":
      return "enum";
    case "message_event":
    case "task_completed":
      return "boolean";
    case "time_since_event":
    case "time_in_stage":
      return "duration";
  }
}

interface ResolvedOperand {
  present: boolean;
  value: FlowConditionLiteral | null;
}

type RuntimeRecord = Record<string, unknown>;

function asRecord(value: unknown): RuntimeRecord | undefined {
  try {
    if (value === null || typeof value !== "object" || Array.isArray(value)) return undefined;
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) return undefined;
    if (Object.getOwnPropertySymbols(value).length > 0) return undefined;

    const normalized = Object.create(null) as RuntimeRecord;
    for (const key of Object.getOwnPropertyNames(value)) {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (!descriptor?.enumerable || !("value" in descriptor)) return undefined;
      normalized[key] = descriptor.value;
    }
    return normalized;
  } catch {
    return undefined;
  }
}

function denseArray(value: unknown): unknown[] | undefined {
  try {
    if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype) return undefined;
    if (Object.getOwnPropertySymbols(value).length > 0) return undefined;

    const lengthDescriptor = Object.getOwnPropertyDescriptor(value, "length");
    if (
      !lengthDescriptor ||
      !("value" in lengthDescriptor) ||
      !Number.isSafeInteger(lengthDescriptor.value) ||
      lengthDescriptor.value < 0
    )
      return undefined;
    const length = lengthDescriptor.value;
    const ownNames = Object.getOwnPropertyNames(value);
    if (ownNames.length !== length + 1 || !ownNames.includes("length")) return undefined;
    for (const key of ownNames) {
      if (key === "length") continue;
      if (!/^(0|[1-9]\d*)$/.test(key) || Number(key) >= length) return undefined;
    }

    const normalized: unknown[] = [];
    for (let index = 0; index < length; index += 1) {
      const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
      if (!descriptor?.enumerable || !("value" in descriptor)) return undefined;
      normalized.push(descriptor.value);
    }
    return normalized;
  } catch {
    return undefined;
  }
}

const INVALID_NORMALIZED_INPUT = Symbol("invalid_flow_condition_input");

function normalizeJsonValue(
  value: unknown,
  seen: WeakSet<object>,
): unknown | typeof INVALID_NORMALIZED_INPUT {
  try {
    if (value === null) return null;
    if (typeof value !== "object") {
      return typeof value === "function" || typeof value === "symbol" || typeof value === "bigint"
        ? INVALID_NORMALIZED_INPUT
        : value;
    }
    if (seen.has(value)) return INVALID_NORMALIZED_INPUT;
    seen.add(value);

    if (Array.isArray(value)) {
      const array = denseArray(value);
      if (!array) return INVALID_NORMALIZED_INPUT;
      const normalized: unknown[] = [];
      for (const item of array) {
        const child = normalizeJsonValue(item, seen);
        if (child === INVALID_NORMALIZED_INPUT) return INVALID_NORMALIZED_INPUT;
        normalized.push(child);
      }
      seen.delete(value);
      return normalized;
    }

    const record = asRecord(value);
    if (!record) return INVALID_NORMALIZED_INPUT;
    const normalized = Object.create(null) as RuntimeRecord;
    for (const key of Object.getOwnPropertyNames(record)) {
      const child = normalizeJsonValue(record[key], seen);
      if (child === INVALID_NORMALIZED_INPUT) return INVALID_NORMALIZED_INPUT;
      normalized[key] = child;
    }
    seen.delete(value);
    return normalized;
  } catch {
    return INVALID_NORMALIZED_INPUT;
  }
}

function nonEmptyText(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function finiteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function validConditionLiteral(value: unknown): boolean {
  if (typeof value === "string" || typeof value === "boolean") return true;
  if (finiteNumber(value)) return true;
  const list = denseArray(value);
  return (
    list !== undefined &&
    list.every((item) => typeof item === "string" || finiteNumber(item))
  );
}

function validBaseLiteral(value: unknown): boolean {
  return value === null || typeof value === "string" || typeof value === "boolean" || finiteNumber(value);
}

function validStringArray(value: unknown): value is readonly string[] {
  const array = denseArray(value);
  return array !== undefined && array.every((item) => typeof item === "string");
}

function validLiteralRecord(
  value: unknown,
  validator: (item: unknown) => boolean,
  allowUndefined = false,
): boolean {
  const record = asRecord(value);
  return Boolean(
    record &&
      Object.values(record).every(
        (item) => (allowUndefined && item === undefined) || validator(item),
      ),
  );
}

function validEvaluationContext(value: unknown): value is FlowEvaluationContext {
  const context = asRecord(value);
  const minutesSinceEvent = asRecord(context?.minutesSinceEvent);
  const relatedMemberships = denseArray(context?.relatedMemberships);
  if (!context || !minutesSinceEvent || !relatedMemberships) return false;
  return (
    nonEmptyText(context.ownerId) &&
    nonEmptyText(context.leadId) &&
    nonEmptyText(context.funnelId) &&
    nonEmptyText(context.now) &&
    Number.isFinite(Date.parse(context.now)) &&
    (context.companyId === undefined || nonEmptyText(context.companyId)) &&
    (context.contactId === undefined || nonEmptyText(context.contactId)) &&
    (context.stageId === undefined || nonEmptyText(context.stageId)) &&
    (context.membershipStatus === undefined ||
      ["active", "won", "lost"].includes(context.membershipStatus as string)) &&
    validLiteralRecord(context.baseFields, validBaseLiteral) &&
    validLiteralRecord(context.customFields, validConditionLiteral, true) &&
    validStringArray(context.tagIds) &&
    validStringArray(context.triggerIds) &&
    validStringArray(context.messageEvents) &&
    validStringArray(context.completedTaskNodeIds) &&
    Object.values(minutesSinceEvent).every(finiteNumber) &&
    (context.minutesInStage === undefined || finiteNumber(context.minutesInStage)) &&
    (context.replyClassification === undefined || nonEmptyText(context.replyClassification)) &&
    (context.approachType === undefined || nonEmptyText(context.approachType)) &&
    relatedMemberships.every((item) => {
      const membership = asRecord(item);
      return Boolean(
        membership &&
          nonEmptyText(membership.funnelId) &&
          ["active", "won", "lost"].includes(membership.status as string),
      );
    })
  );
}

function validEvaluationOptions(value: unknown): value is FlowConditionEvaluationOptions {
  const options = asRecord(value);
  return Boolean(
    options &&
      Object.getOwnPropertyNames(options).every((key) => key === "redactOperand") &&
      (options.redactOperand === undefined || typeof options.redactOperand === "function"),
  );
}

function normalizeEvaluationOptions(value: unknown): FlowConditionEvaluationOptions | undefined {
  const options = asRecord(value);
  if (!options || !Object.getOwnPropertyNames(options).every((key) => key === "redactOperand")) return undefined;
  if (options.redactOperand !== undefined && typeof options.redactOperand !== "function") return undefined;
  return options.redactOperand === undefined
    ? {}
    : { redactOperand: options.redactOperand as NonNullable<FlowConditionEvaluationOptions["redactOperand"]> };
}

function presentText(value: unknown): ResolvedOperand {
  if (value === null || value === undefined) return { present: false, value: null };
  if (typeof value === "string" && value.trim() === "") return { present: false, value: null };
  return { present: true, value: value as FlowConditionLiteral };
}

function resolveOperand(
  operand: FlowConditionOperand,
  context: FlowEvaluationContext,
): ResolvedOperand {
  switch (operand.kind) {
    case "base_field":
      return presentText(context.baseFields[operand.field]);
    case "custom_field":
      return presentText(context.customFields[operand.fieldId]);
    case "tag":
      return { present: true, value: [...context.tagIds] };
    case "trigger":
      return { present: true, value: [...context.triggerIds] };
    case "stage":
      return presentText(context.stageId);
    case "membership_status":
      return presentText(context.membershipStatus);
    case "reply_classification":
      return presentText(context.replyClassification);
    case "approach_type":
      return presentText(context.approachType);
    case "message_event":
      return { present: true, value: context.messageEvents.includes(operand.event) };
    case "task_completed":
      return { present: true, value: context.completedTaskNodeIds.includes(operand.taskNodeId) };
    case "time_since_event": {
      const minutes = context.minutesSinceEvent[operand.event];
      return minutes === undefined ? { present: false, value: null } : { present: true, value: minutes };
    }
    case "time_in_stage":
      return context.minutesInStage === undefined
        ? { present: false, value: null }
        : { present: true, value: context.minutesInStage };
    case "related_membership": {
      const related = context.relatedMemberships.find((item) => item.funnelId === operand.funnelId);
      return related ? { present: true, value: related.status } : { present: false, value: null };
    }
  }
}

/** Normalização de texto: minúsculas, sem acento e sem espaço redundante. */
export function normalizeFlowText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

export function isOperatorAllowed(
  valueType: FlowValueType,
  operator: FlowConditionOperator,
): boolean {
  if (FLOW_PRESENCE_OPERATORS.includes(operator)) return true;
  return flowOperatorsByValueType[valueType].includes(operator);
}

function toComparable(value: FlowConditionLiteral | null, valueType: FlowValueType): number | null {
  if (value === null) return null;
  if (valueType === "date") {
    const time = Date.parse(String(value));
    return Number.isNaN(time) ? null : time;
  }
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function asList(value: FlowConditionLiteral | undefined): readonly (string | number)[] {
  if (value === undefined || typeof value === "boolean") return [];
  if (typeof value === "string" || typeof value === "number") return [value];
  return value;
}

interface ComparisonOutcome {
  result: FlowConditionOutcome;
  reason: string;
}

function compare(
  operator: FlowConditionOperator,
  valueType: FlowValueType,
  resolved: ResolvedOperand,
  literal: FlowConditionLiteral | undefined,
  key: string,
): ComparisonOutcome {
  if (operator === "exists")
    return { result: resolved.present, reason: `${key} ${resolved.present ? "possui" : "não possui"} valor` };
  if (operator === "not_exists")
    return { result: !resolved.present, reason: `${key} ${resolved.present ? "possui" : "não possui"} valor` };

  if (!isOperatorAllowed(valueType, operator))
    return {
      result: "unknown",
      reason: `operador "${operator}" não é permitido para o tipo "${valueType}" em ${key}`,
    };

  if (!resolved.present)
    return { result: "unknown", reason: `${key} não possui valor no contexto avaliado` };

  const value = resolved.value;

  if (valueType === "set") {
    const current = asList(value ?? []);
    if (operator === "is_empty") return { result: current.length === 0, reason: `${key} tem ${current.length} item(ns)` };
    if (operator === "is_not_empty") return { result: current.length > 0, reason: `${key} tem ${current.length} item(ns)` };
    const wanted = asList(literal);
    if (wanted.length === 0)
      return { result: "unknown", reason: `${key} recebeu operador de conjunto sem valor de comparação` };
    const found = wanted.every((item) => current.includes(item));
    if (operator === "includes") return { result: found, reason: `${key} ${found ? "contém" : "não contém"} o conjunto informado` };
    return { result: !found, reason: `${key} ${found ? "contém" : "não contém"} o conjunto informado` };
  }

  if (FLOW_VALUELESS_OPERATORS.includes(operator))
    return { result: "unknown", reason: `operador "${operator}" só se aplica a conjuntos` };

  if (literal === undefined)
    return { result: "unknown", reason: `${key} recebeu "${operator}" sem valor de comparação` };

  if (FLOW_LIST_OPERATORS.includes(operator)) {
    const wanted = asList(literal).map((item) => (valueType === "text" ? normalizeFlowText(String(item)) : item));
    const current = valueType === "text" ? normalizeFlowText(String(value)) : (value as string | number);
    const found = wanted.includes(current);
    if (operator === "in") return { result: found, reason: `${key} ${found ? "está" : "não está"} na lista` };
    return { result: !found, reason: `${key} ${found ? "está" : "não está"} na lista` };
  }

  if (valueType === "text") {
    const current = normalizeFlowText(String(value));
    const wanted = normalizeFlowText(String(literal));
    switch (operator) {
      case "equals":
        return { result: current === wanted, reason: `${key} normalizado "${current}" comparado a "${wanted}"` };
      case "not_equals":
        return { result: current !== wanted, reason: `${key} normalizado "${current}" comparado a "${wanted}"` };
      case "contains":
        return { result: current.includes(wanted), reason: `${key} normalizado "${current}" contém "${wanted}"?` };
      case "starts_with":
        return { result: current.startsWith(wanted), reason: `${key} normalizado "${current}" começa com "${wanted}"?` };
      default:
        return { result: "unknown", reason: `operador "${operator}" não se aplica a texto` };
    }
  }

  if (valueType === "boolean" || valueType === "enum") {
    const equal = String(value) === String(literal);
    if (operator === "equals") return { result: equal, reason: `${key} é "${String(value)}"` };
    if (operator === "not_equals") return { result: !equal, reason: `${key} é "${String(value)}"` };
    return { result: "unknown", reason: `operador "${operator}" não se aplica a "${valueType}"` };
  }

  const left = toComparable(value, valueType);
  const right = toComparable(literal, valueType);
  if (left === null || right === null)
    return { result: "unknown", reason: `${key} não pôde ser comparado numericamente` };
  const detail = `${key} = ${left} comparado a ${right}`;
  switch (operator) {
    case "equals":
      return { result: left === right, reason: detail };
    case "not_equals":
      return { result: left !== right, reason: detail };
    case "greater_than":
      return { result: left > right, reason: detail };
    case "greater_or_equal":
      return { result: left >= right, reason: detail };
    case "less_than":
      return { result: left < right, reason: detail };
    case "less_or_equal":
      return { result: left <= right, reason: detail };
    default:
      return { result: "unknown", reason: `operador "${operator}" não se aplica a "${valueType}"` };
  }
}

function evaluateNode(
  condition: FlowCondition,
  context: FlowEvaluationContext,
  inputs: FlowEvaluatedInput[],
  options: FlowConditionEvaluationOptions,
): ComparisonOutcome {
  if (condition.type === "comparison") {
    const valueType = operandValueType(condition.operand);
    const resolved = resolveOperand(condition.operand, context);
    const key = operandKey(condition.operand);
    const redacted = options.redactOperand?.(condition.operand) === true;
    if (!inputs.some((input) => input.operandKey === key)) {
      inputs.push({
        operandKey: key,
        operandKind: condition.operand.kind,
        valueType,
        present: resolved.present,
        value: redacted ? undefined : resolved.value,
        redacted,
      });
    }
    return compare(condition.operator, valueType, resolved, condition.value, key);
  }

  if (condition.type === "not") {
    const inner = evaluateNode(condition.condition, context, inputs, options);
    if (inner.result === "unknown") return { result: "unknown", reason: `negação indefinida: ${inner.reason}` };
    return { result: !inner.result, reason: `negação de (${inner.reason})` };
  }

  const outcomes = condition.conditions.map((item) => evaluateNode(item, context, inputs, options));
  const reasons = outcomes.map((item) => item.reason).join(" | ");

  if (condition.type === "all") {
    if (outcomes.some((item) => item.result === false))
      return { result: false, reason: `E lógico com ao menos uma condição falsa: ${reasons}` };
    if (outcomes.some((item) => item.result === "unknown"))
      return { result: "unknown", reason: `E lógico com condição indefinida: ${reasons}` };
    return { result: true, reason: `E lógico com todas verdadeiras: ${reasons}` };
  }

  if (outcomes.some((item) => item.result === true))
    return { result: true, reason: `OU lógico com ao menos uma verdadeira: ${reasons}` };
  if (outcomes.some((item) => item.result === "unknown"))
    return { result: "unknown", reason: `OU lógico com condição indefinida: ${reasons}` };
  return { result: false, reason: `OU lógico com todas falsas: ${reasons}` };
}

/**
 * Avalia uma expressão inteira e devolve a trilha explicável exigida pelo
 * inspetor de run: expressão, entradas usadas, resultado e motivo.
 */
export function evaluateFlowCondition(
  condition: FlowCondition,
  context: FlowEvaluationContext,
  options: FlowConditionEvaluationOptions = {},
): FlowConditionEvaluation {
  try {
    const normalizedCondition = normalizeJsonValue(condition, new WeakSet<object>());
    const normalizedContext = normalizeJsonValue(context, new WeakSet<object>());
    const normalizedOptions = normalizeEvaluationOptions(options);
    if (
      normalizedCondition === INVALID_NORMALIZED_INPUT ||
      normalizedContext === INVALID_NORMALIZED_INPUT ||
      !normalizedOptions
    ) return invalidEvaluation();

    const parsedCondition = flowConditionSchema.safeParse(normalizedCondition);
    const safeContext = normalizedContext as FlowEvaluationContext;
    if (!parsedCondition.success || !validEvaluationContext(safeContext) || !validEvaluationOptions(normalizedOptions))
      return invalidEvaluation();

    const inputs: FlowEvaluatedInput[] = [];
    const outcome = evaluateNode(parsedCondition.data, safeContext, inputs, normalizedOptions);
    return { expression: parsedCondition.data, inputs, result: outcome.result, reason: outcome.reason };
  } catch {
    return invalidEvaluation();
  }
}

function invalidEvaluation(): FlowConditionEvaluation {
  return {
    // Não ecoa uma condição malformada: ela pode ser cíclica ou conter
    // getters/proxies que voltariam a quebrar a serialização downstream.
    expression: { type: "comparison", operand: { kind: "stage" }, operator: "exists" },
    inputs: [],
    result: "unknown",
    reason: "A condição ou o contexto de avaliação está fora do contrato.",
  };
}

/** Profundidade da expressão, usada pelo validador para limitar aninhamento. */
export function flowConditionDepth(condition: FlowCondition): number {
  if (condition.type === "comparison") return 1;
  if (condition.type === "not") return 1 + flowConditionDepth(condition.condition);
  return 1 + Math.max(0, ...condition.conditions.map(flowConditionDepth));
}

/** Todos os operandos citados por uma expressão, em ordem de ocorrência. */
export function flowConditionOperands(condition: FlowCondition): FlowConditionOperand[] {
  if (condition.type === "comparison") return [condition.operand];
  if (condition.type === "not") return flowConditionOperands(condition.condition);
  return condition.conditions.flatMap(flowConditionOperands);
}

/** Comparações de uma expressão, usadas para extrair dependências e validar. */
export function flowConditionComparisons(
  condition: FlowCondition,
): Extract<FlowCondition, { type: "comparison" }>[] {
  if (condition.type === "comparison") return [condition];
  if (condition.type === "not") return flowConditionComparisons(condition.condition);
  return condition.conditions.flatMap(flowConditionComparisons);
}
